/**
 * TypeScript types for Deadline/Watchtower frontend components
 */

// Deadline types
export const DEADLINE_TYPES = [
  { value: 'iep', label: 'IEP' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'medical', label: 'Medical' },
  { value: 'legal', label: 'Legal' },
  { value: 'application', label: 'Application' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'other', label: 'Other' },
] as const;

export type DeadlineType = (typeof DEADLINE_TYPES)[number]['value'];

// Deadline priorities
export const DEADLINE_PRIORITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

export type DeadlinePriority = (typeof DEADLINE_PRIORITIES)[number]['value'];

// Deadline statuses
export const DEADLINE_STATUSES = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'due_soon', label: 'Due Soon' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
  { value: 'dismissed', label: 'Dismissed' },
] as const;

export type DeadlineStatus = (typeof DEADLINE_STATUSES)[number]['value'];

// Alert channels
export const ALERT_CHANNELS = [
  { value: 'push', label: 'Push Notification' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
] as const;

export type AlertChannel = (typeof ALERT_CHANNELS)[number]['value'];

// Recurrence patterns
export const RECURRENCE_PATTERNS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

export type RecurrencePattern = (typeof RECURRENCE_PATTERNS)[number]['value'];

// Creation sources
export type CreationSource = 'manual' | 'ai_extracted' | 'template' | 'recurring';

// Alert configuration
export interface DeadlineAlert {
  alertId: string;
  daysBeforeDeadline: number;
  channels: AlertChannel[];
  enabled: boolean;
}

// Deadline summary for list views
export interface DeadlineSummary {
  deadlineId: string;
  caseFileId: string;
  title: string;
  dueDate: string;
  dueTimestamp: { _seconds: number; _nanoseconds: number };
  type: DeadlineType;
  priority: DeadlinePriority;
  status: DeadlineStatus;
  isRecurring: boolean;
  creationSource: CreationSource;
  caseFileName?: string; // For cross-CaseFile views
}

// Full deadline document
export interface Deadline extends DeadlineSummary {
  userId: string;
  description?: string;
  relatedDocumentId?: string;
  alerts: DeadlineAlert[];
  alertsSent: Array<{
    alertId: string;
    sentAt: { _seconds: number };
    channel: AlertChannel;
    status: string;
  }>;
  recurrencePattern?: RecurrencePattern;
  completedAt?: { _seconds: number };
  completedBy?: string;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  metadata?: Record<string, unknown>;
}

// Form data for creating/updating deadlines
export interface DeadlineFormData {
  title: string;
  dueDate: string;
  type: DeadlineType;
  priority: DeadlinePriority;
  description?: string;
  relatedDocumentId?: string;
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  alerts: Array<{
    daysBeforeDeadline: number;
    channels: AlertChannel[];
    enabled: boolean;
  }>;
}

// Priority styles
export const PRIORITY_STYLES: Record<DeadlinePriority, { border: string; bg: string; text: string }> = {
  critical: { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  high: { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' },
  medium: { border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700' },
  low: { border: 'border-gray-400', bg: 'bg-gray-50', text: 'text-gray-600' },
};

// Status styles
export const STATUS_STYLES: Record<DeadlineStatus, { bg: string; text: string }> = {
  overdue: { bg: 'bg-red-100', text: 'text-red-800' },
  due_soon: { bg: 'bg-orange-100', text: 'text-orange-800' },
  upcoming: { bg: 'bg-green-100', text: 'text-green-800' },
  completed: { bg: 'bg-gray-100', text: 'text-gray-600' },
  dismissed: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

// Type colors for badges
export const TYPE_COLORS: Record<DeadlineType, string> = {
  iep: 'bg-purple-100 text-purple-800',
  insurance: 'bg-green-100 text-green-800',
  medical: 'bg-red-100 text-red-800',
  legal: 'bg-blue-100 text-blue-800',
  application: 'bg-cyan-100 text-cyan-800',
  renewal: 'bg-amber-100 text-amber-800',
  appeal: 'bg-pink-100 text-pink-800',
  other: 'bg-gray-100 text-gray-800',
};

// Helper functions
export function getDaysUntil(dueDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatDaysRemaining(dueDate: string): string {
  const days = getDaysUntil(dueDate);
  if (days < 0) {
    const absDays = Math.abs(days);
    return `Overdue by ${absDays} day${absDays !== 1 ? 's' : ''}`;
  }
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `${days} days left`;
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
